import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectAuthUserId } from '../../redux/authSlice';
import { FormField, TextArea } from '../ui/forms';
import FancyButton from '../ui/fancyButton';
import ReflectionViewModal from './ReflectionViewModal';
import FormattedReflection from '../Tasks/FormattedReflection';
import { REFLECTION_QUESTIONS } from '../../config/reflectionQuestions';
import { ReflectionAgentService } from '../../services/llmClient/ReflectionAgentService';
import {
  createReflection,
  fetchReflectionByDate,
  deleteReflection,
} from '../../services/supabaseClient/reflectionService';
import eventStreamService from '../../services/supabaseClient/eventStreamService';
import { fetchUserReflectionQuestions } from '../../services/supabaseClient/profileService';
import Slider from '../ui/Slider';
import { formatDateString, getPreviousDate, getNextDate } from '../../utils/dateUtils';

import './Reflection.css';

const ReflectionEntryForm = ({ date, compact = false }) => {
  const userId = useSelector(selectAuthUserId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingReflection, setExistingReflection] = useState(null);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState(REFLECTION_QUESTIONS);
  const [generatingJournal, setGeneratingJournal] = useState(false);
  const [generatedJournal, setGeneratedJournal] = useState(null);
  const [showJournalPreview, setShowJournalPreview] = useState(false);
  const [journalAccepted, setJournalAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    what_learned: '',
    most_challenging: '',
    aha_moment: '',
    experience_connection: '',
    application: '',
    questions: '',
    understanding_rating: 3,
    engagement_rating: 3,
    reflection_entry: '',
  });

  const buildUserResponses = () => {
    const responses = {};
    questions.forEach((question) => {
      const value = formData[question.id];
      if (value !== undefined && value !== null && value !== '') {
        responses[question.id] = value;
      }
    });
    return responses;
  };

  // Fetch custom questions and existing reflection on mount
  useEffect(() => {
    const loadData = async () => {
      if (!userId || !date) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Fetch custom reflection questions
      const { data: customQuestions } = await fetchUserReflectionQuestions(userId);
      if (customQuestions && customQuestions.length > 0) {
        setQuestions(customQuestions);
      }

      // Fetch existing reflection
      const { data, error: fetchError } = await fetchReflectionByDate(userId, date);

      if (fetchError) {
        setError('Failed to load reflection: ' + fetchError.message);
        setLoading(false);
        return;
      }

      if (data) {
        setExistingReflection(data);
        setFormData({
          ...data.user_responses,
          reflection_entry: data.reflection_entry,
        });
        // If editing existing reflection, mark journal as accepted
        if (data.reflection_entry) {
          setJournalAccepted(true);
        }
      }

      setLoading(false);
    };

    loadData();
  }, [userId, date]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGenerateJournal = async () => {
    setGeneratingJournal(true);
    setError(null);
    void eventStreamService.emitEvent({
      userId,
      sessionId: `reflection:${date}`,
      streamKey: `reflection:${date}`,
      eventFamily: 'reflection',
      eventType: 'reflection_generation_requested',
      eventName: 'reflection_generation_requested',
      origin: 'reflection_entry_form',
      actorType: 'user',
      actorId: userId,
      entityType: 'reflection_day',
      entityId: date,
      reflectionId: date,
    });

    try {
      const userResponses = buildUserResponses();

      const result = await ReflectionAgentService.generateJournalEntry({
        userId,
        date,
        userResponses,
        questions,
        // TODO: Add tasks and projects context when available
        completedTasks: [],
        pendingTasks: [],
        activeProjects: [],
      });

      setGeneratedJournal(result);
      setShowJournalPreview(true);
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_generation_succeeded',
        eventName: 'reflection_generation_succeeded',
        origin: 'reflection_entry_form',
        actorType: 'system',
        actorId: 'reflection_agent',
        entityType: 'reflection_day',
        entityId: date,
        reflectionId: date,
        success: true,
        payload: {
          questionCount: questions.length,
          responseCount: Object.keys(userResponses).length,
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error generating journal:', err);
      setError('Failed to generate journal entry: ' + err.message);
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_generation_failed',
        eventName: 'reflection_generation_failed',
        origin: 'reflection_entry_form',
        actorType: 'system',
        actorId: 'reflection_agent',
        entityType: 'reflection_day',
        entityId: date,
        reflectionId: date,
        success: false,
        errorCode: err?.message || 'reflection_generation_failed',
      });
    } finally {
      setGeneratingJournal(false);
    }
  };

  const handleAcceptJournal = () => {
    if (generatedJournal) {
      handleInputChange('reflection_entry', generatedJournal.journalEntry);
      setShowJournalPreview(false);
      setGeneratedJournal(null);
      setJournalAccepted(true);
    }
  };

  const handleRejectJournal = () => {
    setShowJournalPreview(false);
    setGeneratedJournal(null);
  };

  const handleManualEdit = (value) => {
    handleInputChange('reflection_entry', value);
    // If user manually edits, they need to regenerate or it counts as accepted
    if (value && value.length > 50) {
      setJournalAccepted(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent saving if reflection already exists
    if (existingReflection) {
      alert('A reflection already exists for this date. You cannot modify existing reflections.');
      return;
    }

    setSaving(true);
    setError(null);

    const userResponses = buildUserResponses();

    try {
      // Create new reflection only
      const { data, error: createError } = await createReflection(
        userId,
        date,
        userResponses,
        formData.reflection_entry
      );

      if (createError) throw createError;

      setExistingReflection(data);
      alert('Reflection saved successfully!');
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_saved',
        eventName: 'reflection_saved',
        origin: 'reflection_entry_form',
        actorType: 'user',
        actorId: userId,
        entityType: 'reflection',
        entityId: data?.id || date,
        reflectionId: data?.id || date,
        success: true,
        payload: {
          responseCount: Object.keys(userResponses).length,
          reflectionLength: String(formData.reflection_entry || '').length,
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error saving reflection:', err);
      setError('Failed to save reflection: ' + err.message);
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_save_failed',
        eventName: 'reflection_save_failed',
        origin: 'reflection_entry_form',
        actorType: 'user',
        actorId: userId,
        entityType: 'reflection_day',
        entityId: date,
        reflectionId: date,
        success: false,
        errorCode: err?.message || 'reflection_save_failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this reflection?')) return;

    setSaving(true);
    setError(null);

    try {
      const { error: deleteError } = await deleteReflection(userId, date);

      if (deleteError) throw deleteError;

      alert('Reflection deleted successfully!');
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_deleted',
        eventName: 'reflection_deleted',
        origin: 'reflection_entry_form',
        actorType: 'user',
        actorId: userId,
        entityType: 'reflection_day',
        entityId: date,
        reflectionId: date,
        success: true,
      });
      navigate('/reflection');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error deleting reflection:', err);
      setError('Failed to delete reflection: ' + err.message);
      setSaving(false);
      void eventStreamService.emitEvent({
        userId,
        sessionId: `reflection:${date}`,
        streamKey: `reflection:${date}`,
        eventFamily: 'reflection',
        eventType: 'reflection_delete_failed',
        eventName: 'reflection_delete_failed',
        origin: 'reflection_entry_form',
        actorType: 'user',
        actorId: userId,
        entityType: 'reflection_day',
        entityId: date,
        reflectionId: date,
        success: false,
        errorCode: err?.message || 'reflection_delete_failed',
      });
    }
  };

  const handleBack = () => {
    navigate('/reflection');
  };

  // Using utility functions from dateUtils
  const formatDate = (dateString) => {
    return formatDateString(dateString);
  };

  if (loading) {
    return (
      <div className="reflection-entry-container">
        <div className="reflection-loading">
          <i className="fa-solid fa-spinner fa-spin" /> Loading reflection...
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'reflection-entry-container'}>
      {!compact && (
        <div className="reflection-entry-header">
          <div>
            <h1>Daily Reflection</h1>
            <div className="reflection-date-display">{formatDate(date)}</div>
          </div>
          <div className="date-navigation">
            <button
              type="button"
              className="date-nav-button"
              onClick={() => navigate(`/reflection/${getPreviousDate(date)}`)}
            >
              <i className="fa-solid fa-chevron-left" /> Previous
            </button>
            <button type="button" className="date-nav-button" onClick={handleBack}>
              <i className="fa-solid fa-calendar" /> Calendar
            </button>
            <button
              type="button"
              className="date-nav-button"
              onClick={() => navigate(`/reflection/${getNextDate(date)}`)}
            >
              Next <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </div>
      )}

      {error && <div className="reflection-error">{error}</div>}

      {/* Show existing reflection if it exists */}
      {existingReflection ? (
        <div className="existing-reflection-view">
          <div className="reflection-view-header">
            <h2>
              <i className="fa-solid fa-book-open" /> Your Reflection
            </h2>
            <p className="reflection-date-info">Saved on {formatDate(date)}</p>
          </div>

          <div className="reflection-content">
            <div className="reflection-entry-display">
              <h3>Reflection Entry</h3>
              <FormattedReflection text={existingReflection.reflection_entry} />
            </div>

            {existingReflection.user_responses && (
              <div className="reflection-responses">
                <h3>Your Responses</h3>
                {questions.map((question) => {
                  const response = existingReflection.user_responses[question.id];
                  if (!response && response !== 0) return null;

                  return (
                    <div key={question.id} className="response-item">
                      <strong>{question.question}</strong>
                      <p>{question.type === 'slider' ? `Rating: ${response}/5` : response}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-actions">
            <FancyButton variant="primary" onClick={() => setShowModal(true)}>
              <i className="fa-solid fa-expand" /> View Full Screen
            </FancyButton>
            <FancyButton variant="secondary" onClick={handleDelete} disabled={saving}>
              <i className="fa-solid fa-trash" /> Delete
            </FancyButton>
            <FancyButton variant="secondary" onClick={handleBack} disabled={saving}>
              <i className="fa-solid fa-arrow-left" /> Back to Calendar
            </FancyButton>
          </div>

          {/* Full Screen Modal */}
          {showModal && (
            <ReflectionViewModal
              reflection={existingReflection}
              date={date}
              onClose={() => setShowModal(false)}
              questions={questions}
            />
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={`reflection-form ${compact ? 'compact' : ''}`}>
          {questions.map((question) => (
            <div key={question.id}>
              {question.type === 'textarea' ? (
                <FormField
                  label={question.question}
                  htmlFor={question.id}
                  required={question.required}
                >
                  <TextArea
                    id={question.id}
                    value={formData[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    placeholder={question.placeholder}
                    rows={question.rows || 3}
                    required={question.required}
                  />
                </FormField>
              ) : question.type === 'slider' ? (
                <FormField
                  label={question.question}
                  htmlFor={question.id}
                  required={question.required}
                >
                  <Slider
                    id={question.id}
                    value={
                      formData[question.id] ??
                      Math.round(((question.min ?? 1) + (question.max ?? 5)) / 2)
                    }
                    onChange={(e) => handleInputChange(question.id, parseInt(e.target.value, 10))}
                    min={question.min ?? 1}
                    max={question.max ?? 5}
                    showValue
                    showLabels
                    minLabel="Low"
                    maxLabel="High"
                    required={question.required}
                  />
                </FormField>
              ) : null}
            </div>
          ))}

          <div className="reflection-entry-section">
            <FormField label="Your Reflection" htmlFor="reflection_entry" required>
              <div className="journal-generation-controls">
                <FancyButton
                  variant="secondary"
                  type="button"
                  onClick={handleGenerateJournal}
                  disabled={generatingJournal || !formData.what_learned}
                >
                  {generatingJournal ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles" /> Generate Journal Entry
                    </>
                  )}
                </FancyButton>
              </div>
              <TextArea
                id="reflection_entry"
                value={formData.reflection_entry}
                onChange={(e) => handleManualEdit(e.target.value)}
                placeholder="Click 'Generate Journal Entry' to have AI create a refined journal entry based on your responses above..."
                rows={8}
                required
                disabled={!journalAccepted && !formData.reflection_entry}
              />
              {!journalAccepted && !existingReflection && (
                <div className="journal-requirement-notice">
                  <i className="fa-solid fa-info-circle" />
                  <span>
                    You must generate and review an AI journal entry before saving, or write your
                    own (50+ characters).
                  </span>
                </div>
              )}
            </FormField>
          </div>

          {showJournalPreview && generatedJournal && (
            <div className="journal-preview-modal">
              <div className="journal-preview-content">
                <div className="journal-preview-header">
                  <h3>
                    <i className="fa-solid fa-sparkles" /> Generated Journal Entry
                  </h3>
                  <button
                    type="button"
                    className="close-preview"
                    onClick={handleRejectJournal}
                    aria-label="Close preview"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                <div className="journal-preview-body">
                  <div className="generated-journal-text">
                    <FormattedReflection text={generatedJournal.journalEntry} />
                  </div>

                  {generatedJournal.followUpQuestions &&
                    generatedJournal.followUpQuestions.length > 0 && (
                      <div className="journal-follow-up-questions">
                        <h4>
                          <i className="fa-solid fa-question-circle" /> Follow-up Questions:
                        </h4>
                        <ul>
                          {generatedJournal.followUpQuestions.map((question, idx) => (
                            <li key={idx}>{question}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {generatedJournal.resources && generatedJournal.resources.length > 0 && (
                    <div className="journal-resources">
                      <h4>
                        <i className="fa-solid fa-book" /> Recommended Resources:
                      </h4>
                      <ul>
                        {generatedJournal.resources.map((resource, idx) => (
                          <li key={idx}>
                            <a href={resource.url} target="_blank" rel="noopener noreferrer">
                              {resource.title}
                            </a>
                            {resource.type && (
                              <span className="resource-type"> ({resource.type})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedJournal.insights && generatedJournal.insights.length > 0 && (
                    <div className="journal-insights">
                      <h4>
                        <i className="fa-solid fa-lightbulb" /> Key Insights:
                      </h4>
                      <ul>
                        {generatedJournal.insights.map((insight, idx) => (
                          <li key={idx}>{insight}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="journal-preview-actions">
                  <FancyButton variant="secondary" onClick={handleGenerateJournal}>
                    <i className="fa-solid fa-rotate" /> Regenerate
                  </FancyButton>
                  <FancyButton variant="primary" onClick={handleAcceptJournal}>
                    <i className="fa-solid fa-check" /> Accept & Use
                  </FancyButton>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <FancyButton variant="secondary" onClick={handleBack} disabled={saving}>
              Cancel
            </FancyButton>
            <FancyButton
              variant="primary"
              type="submit"
              disabled={saving || !journalAccepted}
              title={!journalAccepted ? 'Generate and accept a journal entry first' : ''}
            >
              {saving ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save" /> Save Reflection
                </>
              )}
            </FancyButton>
          </div>
        </form>
      )}
    </div>
  );
};

export default ReflectionEntryForm;
